/**
 * Sprint Backlog Component
 * Displays unassigned tasks that can be added to sprint
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SearchIcon from '@mui/icons-material/Search';
import BacklogIcon from '@mui/icons-material/Inbox';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { addTaskToSprint } from '../../store/sprintSlice';
import { showToast } from '../../store/uiSlice';
import { SprintTask } from '../../entities/sprint/model/types';
import './SprintBacklog.scss';

interface SprintBacklogProps {
  sprintId: string;
  backlogTasks?: SprintTask[];
}

const SprintBacklog: React.FC<SprintBacklogProps> = ({ sprintId, backlogTasks = [] }) => {
  const dispatch = useAppDispatch();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPriority, setSelectedPriority] = useState<string | null>(null);

  const filteredTasks = backlogTasks.filter((task) => {
    const matchesSearch = task.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPriority = !selectedPriority || task.priority === selectedPriority;
    return matchesSearch && matchesPriority;
  });

  const handleAddToSprint = async (taskId: string) => {
    try {
      await dispatch(addTaskToSprint({ sprintId, taskId })).unwrap();
    } catch (error: any) {
      dispatch(
        showToast({
          message: (typeof error === 'string' ? error : error?.message) || 'Failed to add task to sprint',
          type: 'error',
        })
      );
    }
  };

  return (
    <Card className="sprint-backlog">
      <CardContent>
        <Box className="backlog-header">
          <Box className="header-left">
            <BacklogIcon color="primary" />
            <Typography variant="h6">Sprint Backlog</Typography>
            <Chip label={`${backlogTasks.length} tasks`} size="small" />
          </Box>
        </Box>

        <Box className="backlog-filters">
          <TextField
            fullWidth
            size="small"
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Box className="priority-filters">
            {['High', 'Medium', 'Low'].map((priority) => (
              <Chip
                key={priority}
                label={priority}
                onClick={() => setSelectedPriority(selectedPriority === priority ? null : priority)}
                color={selectedPriority === priority ? 'primary' : 'default'}
                variant={selectedPriority === priority ? 'filled' : 'outlined'}
                size="small"
              />
            ))}
          </Box>
        </Box>

        <List className="backlog-list">
          {filteredTasks.length > 0 ? (
            filteredTasks.map((task) => (
              <ListItem
                key={task.id}
                className="backlog-item"
                secondaryAction={
                  <IconButton
                    edge="end"
                    color="primary"
                    onClick={() => handleAddToSprint(task.id)}
                  >
                    <AddCircleIcon />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={task.name}
                  secondary={
                    <Box className="task-meta">
                      {task.priority && (
                        <Chip label={task.priority} size="small" className={`priority-${task.priority.toLowerCase()}`} />
                      )}
                      {task.storyPoints && <Chip label={`${task.storyPoints} pts`} size="small" variant="outlined" />}
                    </Box>
                  }
                />
              </ListItem>
            ))
          ) : (
            <Box className="empty-state">
              <BacklogIcon fontSize="large" color="disabled" />
              <Typography variant="body2" color="text.secondary">
                {searchQuery || selectedPriority ? 'No tasks match your filters' : 'No tasks in backlog'}
              </Typography>
            </Box>
          )}
        </List>
      </CardContent>
    </Card>
  );
};

export default SprintBacklog;
